import React from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Localize } from "components/service";
import { timeFormat } from "utils/helper-functions";
import TranslationAiPricing from "./translation-ai-pricing-helper";
import TranslationAiFormat from "./translation-ai-format-helper";
import TranslationAiLanguage from "./translation-ai-language-helper";


const emptyValue = <strong>&mdash;</strong>;

const entityRequestFilter = {
    column: "PROMPT_TYPE",
    operator: "EQ",
    value: "entity",
};

const withEntityRequestFilter = (filter) => (
    filter ? {
        ...entityRequestFilter,
        AND: [ filter ],
    } : entityRequestFilter
);

const renderValue = (value) => value || value === 0 ? value : emptyValue;

const renderNumber = (value) => TranslationAiFormat.formatInteger(value) || emptyValue;

const columnTitle = (label, tooltip = null) => (
    <span className="translation-ai-column-title">
        <Localize>{ label }</Localize>
        { tooltip &&
            <Tooltip title={ <Localize>{ tooltip }</Localize> }>
                <InfoCircleOutlined />
            </Tooltip>
        }
    </span>
);

const renderPriceResult = ({ value, source }) => {
    if (source === TranslationAiPricing.PRICE_SOURCE_CACHE)
    {
        return <span className="translation-ai-price-note"><Localize>TABLES.Value_FromCache</Localize></span>;
    }

    if (source === TranslationAiPricing.PRICE_SOURCE_FREE)
    {
        return <span className="translation-ai-price-note"><Localize>TABLES.Value_Free</Localize></span>;
    }

    const formattedPrice = TranslationAiFormat.formatPriceValue(value);

    if (!formattedPrice)
    {
        return emptyValue;
    }

    if (source === TranslationAiPricing.PRICE_SOURCE_ESTIMATED)
    {
        return (
            <Tooltip title={ <Localize>TABLES.Tooltip_EstimatedCost</Localize> }>
                <span className="translation-ai-price-estimated">&asymp; { formattedPrice }</span>
            </Tooltip>
        );
    }

    return formattedPrice;
};

const renderPrice = (valueOrRecord, field = 'total_tokens_price') => {
    if (valueOrRecord && typeof valueOrRecord === 'object' && !React.isValidElement(valueOrRecord))
    {
        return renderPriceResult(TranslationAiPricing.resolvePrice(valueOrRecord, field));
    }

    const value = TranslationAiFormat.toNumber(valueOrRecord);

    return value || value === 0 ? renderPriceResult({
        value,
        source: value > 0 ? TranslationAiPricing.PRICE_SOURCE_BACKEND : TranslationAiPricing.PRICE_SOURCE_FREE,
    }) : emptyValue;
};

const renderLanguage = (value, languageMap = {}) => {
    if (!value)
    {
        return emptyValue;
    }

    const languageName = TranslationAiLanguage.getLanguageLabel(value, languageMap);

    return (
        <span className="translation-ai-language-cell">
            <span>{ languageName }</span>
        </span>
    );
};

const renderDate = (value, format) => value ? timeFormat(value, format) : emptyValue;

const renderUser = (request) => {
    const user = request?.relUser;

    if (!user)
    {
        return request?.user_id ? `User #${ request.user_id }` : emptyValue;
    }

    const fullName = [ user.name, user.surname ].filter(Boolean).join(' ');

    return fullName || user.email || user.id || emptyValue;
};

const usageColumns = [
    {
        title: <Localize>TABLES.Column_Title_Date</Localize>,
        dataIndex: "date_at",
    },
    {
        title: <Localize>TABLES.Column_Title_Requests</Localize>,
        dataIndex: "requests_count",
    },
    {
        title: columnTitle('TABLES.Column_Title_Items', 'TABLES.Tooltip_Items'),
        dataIndex: "items_count",
    },
    {
        title: columnTitle('TABLES.Column_Title_TotalTokens', 'TABLES.Tooltip_Tokens'),
        dataIndex: "total_tokens",
    },
    {
        title: columnTitle('TABLES.Column_Title_TotalPrice', 'TABLES.Tooltip_Cost'),
        dataIndex: "total_tokens_price",
    },
];

const requestsColumns = [
    {
        title: <Localize>TABLES.Column_Title_Created</Localize>,
        dataIndex: "created_at",
        columnIndex: "CREATED_AT",
        sorter: true,
    },
    {
        title: <Localize>TABLES.Column_Title_User</Localize>,
        dataIndex: "user",
        columnIndex: "USER_ID",
        sorter: true,
    },
    {
        title: <Localize>TABLES.Column_Title_Model</Localize>,
        dataIndex: "model",
        columnIndex: "MODEL",
        sorter: true,
    },
    {
        title: <Localize>TABLES.Column_Title_TargetLanguage</Localize>,
        dataIndex: "lang_target",
        columnIndex: "LANG_TARGET",
        sorter: true,
    },
    {
        title: columnTitle('TABLES.Column_Title_Items', 'TABLES.Tooltip_Items'),
        dataIndex: "items_count",
        columnIndex: "ITEMS_COUNT",
        sorter: true,
    },
    {
        title: columnTitle('TABLES.Column_Title_TotalPrice', 'TABLES.Tooltip_Cost'),
        dataIndex: "total_tokens_price",
        columnIndex: "TOTAL_TOKENS_PRICE",
        sorter: true,
    },
];

const usageData = (items) => {
    if (!items)
    {
        return [];
    }

    return items.map((item) => ({
        key: item.id || item.date_at,
        date_at: item.date_at === 'unknown' ? emptyValue : renderDate(item.date_at, 'DD.MM.YYYY'),
        requests_count: renderNumber(item.requests_count),
        items_count: renderNumber(item.items_count),
        total_tokens: renderNumber(item.total_tokens),
        total_tokens_price: renderPrice(item.total_tokens_price),
    }));
};

const requestsData = (items, currentObj = {}) => {
    if (!items)
    {
        return [];
    }

    const { languageMap = {} } = currentObj;

    return items.map((item) => ({
        key: item.id,
        created_at: renderDate(item.created_at),
        user: renderUser(item),
        model: renderValue(item.model),
        lang_target: renderLanguage(item.lang_target, languageMap),
        items_count: renderNumber(item.items_count),
        total_tokens_price: renderPrice(item),
    }));
};

const TranslationAiTableHelper = {
    Usage: {
        columns: usageColumns,
        data: usageData,
    },
    Requests: {
        columns: requestsColumns,
        data: requestsData,
    },
    renderPrice,
    renderValue,
    renderNumber,
    withEntityRequestFilter,
};

export default TranslationAiTableHelper;
